// Amplitude Analytics initialization and helpers.
// SDK docs: https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2
//
// Compliance: see docs/analytics-compliance.md for the full rules.
// Summary:
// - Never send raw PII (emails, phone numbers, full names, message bodies).
// - Sensitive identifiers (user_id, seeker_id, mediator_id, consultation_id,
//   idempotency_key, click_id, tokens) are hashed to short opaque digests.
// - Free-text fields (server messages, error messages) are truncated to
//   MAX_STRING_LEN and stripped of emails / UUIDs.
// - Only primitives (string | number | boolean | null) are forwarded — nested
//   objects and arrays are dropped so we never leak an unreviewed shape.
import * as amplitude from '@amplitude/analytics-browser';

const AMPLITUDE_API_KEY =
  (import.meta.env.VITE_AMPLITUDE_API_KEY as string | undefined) ||
  'REPLACE_WITH_AMPLITUDE_API_KEY';

// Maximum characters kept for any free-text string forwarded to Amplitude.
export const MAX_STRING_LEN = 140;

// Property keys that must be hashed rather than sent in the clear.
const HASHED_KEYS = new Set([
  'user_id', 'userId',
  'seeker_id', 'seekerId',
  'mediator_id', 'mediatorId',
  'consultation_id', 'consultationId',
  'other_party_id', 'otherPartyId',
  'idempotency_key', 'idempotencyKey',
  'click_id', 'clickId',
  'priorClickId', 'prior_click_id',
  'token', 'access_token', 'refresh_token',
  'email', 'phone', 'phone_number',
]);

// Property keys that must be dropped entirely.
const DROPPED_KEYS = new Set([
  'password', 'card', 'card_number', 'cvc', 'cvv',
  'message', 'body', 'description', 'notes', 'full_name', 'name',
  'address', 'iban', 'ssn',
]);

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const LONG_HEX_RE = /\b[0-9a-f]{24,}\b/gi;

// Non-cryptographic fast hash — good enough to give an opaque, stable digest
// for grouping events without exposing the underlying id.
const shortHash = (input: string): string => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return 'h_' + (h >>> 0).toString(36);
};

const scrubString = (raw: string): string => {
  const cleaned = raw
    .replace(EMAIL_RE, '[email]')
    .replace(UUID_RE, '[uuid]')
    .replace(LONG_HEX_RE, '[hex]');
  return cleaned.length > MAX_STRING_LEN
    ? cleaned.slice(0, MAX_STRING_LEN) + '…'
    : cleaned;
};

export const sanitizePayload = (
  input?: Record<string, unknown>,
): Record<string, string | number | boolean | null> => {
  const out: Record<string, string | number | boolean | null> = {};
  if (!input) return out;
  for (const [key, value] of Object.entries(input)) {
    if (DROPPED_KEYS.has(key)) continue;
    if (value == null) { out[key] = null; continue; }

    if (HASHED_KEYS.has(key)) {
      out[key] = typeof value === 'string' || typeof value === 'number'
        ? shortHash(String(value))
        : null;
      continue;
    }

    if (typeof value === 'string') {
      out[key] = scrubString(value);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    } else {
      // Drop objects / arrays — they must be flattened explicitly by callers.
      // This prevents accidental leakage of unreviewed shapes.
      continue;
    }
  }
  return out;
};

let initialized = false;

export const initAmplitude = () => {
  if (initialized) return;
  if (!AMPLITUDE_API_KEY || AMPLITUDE_API_KEY === 'REPLACE_WITH_AMPLITUDE_API_KEY') {
    console.warn('[amplitude] API key missing — analytics disabled.');
    return;
  }

  amplitude.init(AMPLITUDE_API_KEY, {
    autocapture: {
      attribution: true,
      pageViews: false,
      sessions: true,
      formInteractions: false, // may capture input values — off for compliance
      fileDownloads: true,
      elementInteractions: false,
    },
  });

  initialized = true;
};

export const trackPageViewed = (path: string, extra?: Record<string, unknown>) => {
  if (!initialized) return;
  amplitude.track('page_viewed', sanitizePayload({
    path,
    // Only forward the pathname — never full URL (may carry tokens/emails in
    // query strings) or document.referrer (external URLs).
    ...extra,
  }));
};

export const trackButtonClick = (
  buttonName: string,
  extra?: Record<string, unknown>,
) => {
  if (!initialized) return;
  amplitude.track('button_clicked', sanitizePayload({
    button_name: buttonName,
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...extra,
  }));
};

export const identifyUser = (userId: string, traits?: Record<string, unknown>) => {
  if (!initialized) return;
  // We hash the user id before sending to Amplitude so the analytics store
  // never holds a value that can be joined back to auth.users directly.
  amplitude.setUserId(shortHash(userId));
  if (traits) {
    const safe = sanitizePayload(traits);
    const identify = new amplitude.Identify();
    for (const [k, v] of Object.entries(safe)) {
      if (v !== null) identify.set(k, v as string | number | boolean);
    }
    amplitude.identify(identify);
  }
};

export const resetAmplitudeUser = () => {
  if (!initialized) return;
  amplitude.reset();
};
